import { MetaElementUsageIdentityVO } from '@shared/vo/meta-element.vo';
import { SelectionArticleGroupIdentity } from '@project/components/article/model/vo/selection-article-group-identity';
import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { Intersection } from 'three';
import { SelectionId } from '../service/selection-model.service';

export type SelectionType = "metaElement" | "article" | "selectionMetaElement";

export interface SelectionData {
  selectionId: SelectionId;
  isSelected: boolean;
  selectionType: SelectionType;
}

export interface SurfaceSelectionData {
  intersect: Intersection;
  ctrlKey: boolean;
}

@Injectable({ providedIn: 'root' })
export class SelectionModel {

  private _selectedObjects: SelectionId[] = [];

  private viewFirstSelectedSubject: Subject<void> = new Subject<void>();

  public get viewFirstSelected$(): Observable<void> {
    return this.viewFirstSelectedSubject.asObservable();
  }

  private _selectionChanged: Subject<SelectionData[]> = new Subject<SelectionData[]>();

  /** selection listens for selection AND deselection - so it's triggered twice
   if an article is selected and the previous one is automatically deselected.*/
  public get selectionChanged$(): Observable<SelectionData[]> {
    return this._selectionChanged.asObservable();
  }

  private _surfaceSelectionChanged = new Subject<SurfaceSelectionData>();

  public get surfaceSelectionChanged$() {
    return this._surfaceSelectionChanged.asObservable();
  }

  private _clearSelectionRequested: Subject<void> = new Subject<void>();

  public get clearSelectionRequested$() {
    return this._clearSelectionRequested.asObservable();
  }

  public get selectedObjects(): SelectionId[] {
    return this._selectedObjects;
  }

  public set selectedObjects(value: SelectionId[]) {
    this._selectedObjects = value;
  }

  public viewFirstSelected(): void {
    this.viewFirstSelectedSubject.next();
  }

  public notifySelectionChanged(selectionData: SelectionData[]): void {
    if (selectionData.length > 0) {
      this._selectionChanged.next(selectionData);
    }
  }

  public notifySingleSelectionChanged(selectionData: SelectionData) {
    this._selectionChanged.next([selectionData]);
  }

  public isConnectionSelection(): boolean {
    const firstSelection: any = this.getFirstSelection();
    if (!firstSelection) {
      return false;
    }
    return firstSelection instanceof MetaElementUsageIdentityVO;
  }

  public isArticleSelection(): boolean {
    const firstSelection: any = this.getFirstSelection();
    if (!firstSelection) {
      return false;
    }
    return firstSelection instanceof SelectionArticleGroupIdentity;
  }

  private getFirstSelection(): SelectionId | undefined {
    return this._selectedObjects[0];
  }

  public getArticleUsageIds(): string[] {
    let result: string[] = [];
    for (const selectedObject of this._selectedObjects) {
      if (selectedObject instanceof SelectionArticleGroupIdentity) {
        result = result.concat((selectedObject as SelectionArticleGroupIdentity).articleUsageIds);
      }
    }
    return result;
  }

  public get selectedMetaElementUsageIds(): MetaElementUsageIdentityVO[] {
    return this._selectedObjects.filter((item): item is MetaElementUsageIdentityVO => item instanceof MetaElementUsageIdentityVO);
  }

  public get selectedArticleGroupIds(): SelectionArticleGroupIdentity[] {
    return this._selectedObjects.filter((item): item is SelectionArticleGroupIdentity => item instanceof SelectionArticleGroupIdentity);
  }

  public notifySurfaceSelected(surfaceSelectionData: SurfaceSelectionData) {
    this._surfaceSelectionChanged.next(surfaceSelectionData);
  }

  public requestClearSelection() {
    this._clearSelectionRequested.next();
  }
}
